import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { VariableOption } from "@/types/formula";
import { groupSuggestions, namespaceFor, suggestPaths } from "@/utils/formula";

type VariableSuggestionsProps = {
    /** The catalogue, in the order the server sent it. Never re-sorted. */
    options: VariableOption[];
    /** The path fragment typed so far, which decides both the scope and the filter. */
    query: string;
    loading?: boolean;
    /** `continues` says the path is a way in rather than a value: keep completing it. */
    onPick: (path: string, continues: boolean) => void;
    className?: string;
    /** Placement computed by the host, e.g. under the caret. */
    style?: CSSProperties;
};

/**
 * The variables offered for what has been typed, under the catalogue's own headings.
 *
 * Grouped because a flat list of four hundred paths is not an autocompletion: an author
 * looking for a phone number needs to see that « Contact » holds six names worth reading
 * before « Opportunity » holds ninety. The headings, their order, the labels, the types and
 * the descriptions are all the server's — this component decides nothing about them, which
 * is what keeps a column added to a carrier model suggested on the next deploy.
 *
 * A namespace is a way in and not a value. Its children cannot be enumerated — there is one
 * attempt counter per step id and only the graph knows those — so instead of an empty list
 * the author is told what goes after the dot, in the words the catalogue carries.
 */
export function VariableSuggestions({ options, query, loading, onPick, className, style }: VariableSuggestionsProps) {
    const groups = groupSuggestions(suggestPaths(options, query));
    const namespace = namespaceFor(options, query);

    if (loading) {
        return (
            <div style={style} className={cn("rounded border bg-white p-2 text-xs text-gray-500", className)} data-testid="variable-suggestions">
                Loading the variables…
            </div>
        );
    }

    return (
        <div style={style} className={cn("max-h-64 overflow-y-auto rounded border bg-white shadow-md", className)} role="listbox" aria-label="Variables" data-testid="variable-suggestions">
            {namespace && (
                <p className="border-b bg-sky-50 px-2 py-1.5 text-[11px] leading-snug text-sky-900" data-testid="namespace-hint">
                    <span className="font-mono">{namespace.value}.</span> takes any name after the dot, so nothing can list them.
                    {namespace.description ? ` ${namespace.description}` : ""}
                    {namespace.example ? (
                        <>
                            {" "}
                            For example <span className="font-mono">{namespace.example}</span>.
                        </>
                    ) : null}
                </p>
            )}

            {groups.length === 0 && !namespace && <p className="px-2 py-1.5 text-xs text-gray-500">No variable matches « {query} ».</p>}

            {groups.map(group => (
                <div key={group.group}>
                    <p className="sticky top-0 bg-gray-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{group.group}</p>
                    {group.items.map(item => (
                        <button
                            key={item.path}
                            type="button"
                            role="option"
                            aria-selected={false}
                            data-testid={`variable-option-${item.path}`}
                            /* The list is dismissed by a blur, so the mousedown has to do the
                               work: by the time a click fires the input has lost the focus and
                               the caret this insertion is relative to. */
                            onMouseDown={event => {
                                event.preventDefault();
                                onPick(item.path, item.continues);
                            }}
                            className="flex w-full items-baseline gap-2 px-2 py-1 text-left hover:bg-sky-50"
                        >
                            <span className="min-w-0 flex-1">
                                <span className="block truncate font-mono text-[11px] text-gray-900">
                                    {item.segment}
                                    {item.continues ? "…" : ""}
                                </span>
                                {/* A container's label is its own segment, and printing a
                                    name twice reads as a defect rather than as a hint. */}
                                {(item.description || item.label !== item.segment) && <span className="block truncate text-[11px] text-gray-500">{item.description || item.label}</span>}
                            </span>
                            {item.type && <span className="shrink-0 rounded bg-gray-100 px-1 text-[10px] uppercase text-gray-600">{item.type}</span>}
                        </button>
                    ))}
                </div>
            ))}
        </div>
    );
}
